import { yupResolver } from '@hookform/resolvers/yup'
import Container from '@mui/material/Container'

import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import uniqBy from 'lodash/uniqBy'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import * as Yup from 'yup'

import { useNavigate } from 'react-router-dom'
import PhishCampaignEmailCreateFormFollowUp from './PhishCampaignEmailCreateFormFollowUp'
import PhishCampaignEmailCreateFormGeneral from './PhishCampaignEmailCreateFormGeneral'
import { useRef, useState } from 'react'
import combineDateTime from '../../libs/utils/combineDateTime'

const mapStateToProps = () => ({})

const PhishCampaignEmailCreate = ({ dispatch }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const itemRefs = useRef([])

  const [createList, setCreateList] = useState([])

  const companyId = useSelector(state => state.auth.account?.companyId)

  Yup.addMethod(Yup.date, 'minStartDate', function testStartDate(errorMessage1, errorMessage2) {
    return this.test('test-start-date', (errorMessage1, errorMessage2), (_, context) => {
      const { parent = {}, path, createError } = context

      const { startDate, startTime, dateRangeType } = parent

      const isStartFixed = isEqual(dateRangeType, '1')
      const startDateTime = combineDateTime(startDate, startTime)

      // 20240418 if now is 11:00 ensure when I set time is 11:15 can be pass validation
      // remamber can't use isSameOrAfter this function that time must after 15 minutes.
      // meaning if now is 11:00, your select time must 11:16
      if (moment(startDateTime).isAfter(moment().add(14, 'minutes')) && !isStartFixed) {
        return true
      }
      if (moment(startDateTime).isAfter(moment().subtract(1, 'minutes')) && isStartFixed) {
        return true
      }

      // isStartFixed === true === now, isStartFixed === false === + 15 minutes
      return isStartFixed
        ? createError({ path, message: errorMessage2 })
        : createError({ path, message: errorMessage1 })
    })
  })

  Yup.addMethod(Yup.date, 'afterStartDate', function testEndDate(errorMessage) {
    return this.test('test-end-date', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const { startDate, startTime, endDate, endTime } = parent

      const startDateTime = combineDateTime(startDate, startTime)
      const endDateTime = combineDateTime(endDate, endTime)

      // 20240418 if now is 20240418 11:00 ensure when I set time is 20240418 11:15 can be pass validation
      // remamber can't use isSameOrAfter this function that time must after 15 minutes.
      // meaning if now is 11:00, your select time must 11:16
      if (
        moment(endDateTime).isAfter(moment(startDateTime).add(1, 'days').subtract(1, 'minutes'))
      ) {
        return true
      }

      return createError({ path, message: errorMessage })
    })
  })

  Yup.addMethod(Yup.string, 'courseId', function testCourseId(errorMessage) {
    return this.test('test-course-id', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const { checkFollowUp, courseId } = parent

      if (!checkFollowUp) {
        return true
      }

      if (!isEmpty(courseId)) {
        return true
      }

      return createError({ path, message: errorMessage })
    })
  })

  Yup.addMethod(Yup.object, 'emailTemplate', function testEmailTemplate(errorMessage) {
    return this.test('test-email-template', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const { phishType, emailTemplate } = parent

      if (phishType === 'R' || phishType === 'T') {
        return true
      }

      if (!isEmpty(emailTemplate)) {
        return true
      }

      return createError({ path, message: errorMessage })
    })
  })

  Yup.addMethod(Yup.object, 'emailTemplateLabel', function testEmailTemplateLabel(errorMessage) {
    return this.test('test-email-template-label', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const { phishType, emailTemplateLabel } = parent

      if (phishType !== 'R') {
        return true
      }

      if (!isEmpty(emailTemplateLabel)) {
        return true
      }

      return createError({ path, message: errorMessage })
    })
  })

  Yup.addMethod(Yup.object, 'landingPage', function testLandingPage(errorMessage) {
    return this.test('test-landing-page', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const { landingType, landingPage } = parent

      if (landingType === 'default') {
        return true
      }

      if (!isEmpty(landingPage)) {
        return true
      }

      return createError({ path, message: errorMessage })
    })
  })

  Yup.addMethod(Yup.array, 'checkReminderDates', function testReminderDate(errorMessage) {
    return this.test('test-reminder-date', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const {
        checkFollowUp,
        startDate,
        endDate,
        startTime,
        endTime,
        reminderScheduleList = [],
      } = parent

      if (!checkFollowUp) {
        return true
      }

      const startDateTime = combineDateTime(startDate, startTime)
      const endDateTime = combineDateTime(endDate, endTime)

      const error = createError({ path, message: errorMessage })

      const outofRangeDate = reminderScheduleList.find(
        d =>
          !moment(d).isBetween(
            moment(startDateTime).endOf('date'),
            moment(endDateTime).startOf('date'),
            undefined,
            '{}',
          ),
      )

      if (outofRangeDate) {
        return error
      }

      const hasDuplicate =
        uniqBy(reminderScheduleList, element => element.getTime()).length !==
        reminderScheduleList.length

      if (hasDuplicate) {
        return error
      }

      return true
    })
  })

  const validations = {
    name: Yup.string().required(t('validation.campaign_name_required')),
    testCompany: Yup.object()
      .required(t('validation.test_company_required'))
      .typeError(t('validation.test_company_required')),
    courseId: Yup.string().courseId(t('validation.course_required')),
    // reminderScheduleList: Yup.array().checkReminderDates(t('validation.reminder_dates')),
  }

  const form = useForm({
    resolver: yupResolver(Yup.object().shape(validations)),
  })

  const { getValues, watch } = form

  const checkFollowUp = watch('checkFollowUp')

  const onTestsubmit = () => {
    const TestPhishCampaign = []
    const formValues = getValues()

    const { campaignType, testCompany } = formValues

    itemRefs.current?.forEach?.((r, index) => {
      const childFormValues = r.form?.getValues?.() || {}

      const {
        emailTemplate,
        emailTemplateLabel,
        endDate,
        endTime,
        landingPage,
        landingType,
        phishType,
        sendingDateRangeType,
        startDate,
        startTime,
      } = childFormValues

      // const isPhishRandom = phishType === 'R'
      const isLandingDefault = landingType === 'default'

      const startDateTime = combineDateTime(startDate, startTime)
      const endDateTime = combineDateTime(endDate, endTime)

      TestPhishCampaign.push({
        type: campaignType,
        tempModel: phishType,
        phishEmailType: phishType === 'R' || phishType === 'T' ? 'labelId' : 'templateId',
        ...(phishType === 'R' || phishType === 'T'
          ? { phishEmailLabelId: emailTemplateLabel?.id }
          : { phishEmailTempId: emailTemplate.id }),
        landingpageType: isLandingDefault ? 'default' : 'templateId',
        ...(isLandingDefault ? {} : { landingpageTempId: landingPage.id }),
        scheduledStartTime: startDateTime,
        scheduledEndlTime: endDateTime,
        testCompanyName: testCompany.label,
        testCompanyId: testCompany.id,
        timeModel: sendingDateRangeType,
        sendingTimes:
          sendingDateRangeType !== '1'
            ? createList[index]?.sendingTimes.flatMap(time => time.dateTime)
            : [] ?? [],
      })
    })

    dispatch({
      type: 'phishCampaign/sendPhishCampaignTest',
      payload: {
        TestPhishCampaign,
      },
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: {
            message: t('messages.test_campaign_sent'),
            severity: 'success',
          },
        })
      },
      errorCallback: err => {
        dispatch({
          type: 'app/notify',
          payload: { message: err, severity: 'error' },
        })
      },
    })
  }

  const onSubmit = () => {
    const campaignList = []
    const formValues = getValues()

    const {
      attachmentOpen,
      campaignType,
      campaignRunType,
      dataEntered,
      followUpAction,
      linkClicked,
      name,
      testCompany,
      courseName,
      courseId,
      completeNotify,
      reminderNotify,
      sendInvite,
      reminderScheduleList,
    } = formValues

    itemRefs.current?.forEach?.((r, index) => {
      const childFormValues = r.form?.getValues?.() || {}

      const {
        name: childName,
        emailTemplate,
        emailTemplateLabel,
        endDate,
        endTime,
        landingPage,
        // landingPageLabel,
        landingType,
        // phishEmailLabelType,
        phishType,
        sendingDateRangeType,
        startDate,
        startTime,
        userGroup,
      } = childFormValues

      // const isPhishRandom = phishType === 'R'
      const isLandingDefault = landingType === 'default'

      const startDateTime = combineDateTime(startDate, startTime)
      const endDateTime = combineDateTime(endDate, endTime)

      const followUpActions = []

      if (linkClicked) {
        followUpActions.push(1)
      }

      if (dataEntered) {
        followUpActions.push(2)
      }

      if (attachmentOpen) {
        followUpActions.push(3)
      }

      campaignList.push({
        campaignName: createList.length === 1 ? name : childName,
        type: campaignType,
        userGroupId: userGroup.id,
        tempModel: phishType,
        phishEmailType: phishType === 'R' || phishType === 'T' ? 'labelId' : 'templateId',
        ...(phishType === 'R' || phishType === 'T'
          ? { phishEmailLabelId: emailTemplateLabel?.id }
          : { phishEmailTempId: emailTemplate.id }),
        landingpageType: isLandingDefault ? 'default' : 'templateId',
        ...(isLandingDefault ? {} : { landingpageTempId: landingPage.id }),
        scheduledStartTime: startDateTime,
        scheduledEndlTime: endDateTime,
        // ...(followUpAction !== '1' ? { phishCampaignActionFollowUp: followUpActions } : {}),
        courseCampName: courseName,
        courseProgramId: courseId,
        notiCompleteOrNot: completeNotify,
        reminderOrNot: reminderNotify,
        ...(reminderNotify ? { remindTime: reminderScheduleList } : {}),
        invitationOrNot: sendInvite,
        testCompanyId: testCompany.id,
        timeModel: sendingDateRangeType,
        sendingTimes:
          sendingDateRangeType !== '1'
            ? createList[index]?.sendingTimes?.flatMap(time => time.dateTime)
            : [] ?? [],
      })
    })

    dispatch({
      type: 'phishCampaign/createPhishCampaign',
      payload: {
        mainName: name,
        type: campaignType,
        runType: campaignRunType,
        companyId,
        testCompanyId: testCompany.id,
        campaignList,
      },
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: {
            message: t('messages.phish_campaign_created'),
            severity: 'success',
          },
        })
        navigate('/phish_campaign_email', { replace: true })
      },
    })
  }

  return (
    <Container maxWidth={false}>
      {checkFollowUp ? (
        <PhishCampaignEmailCreateFormFollowUp form={form} onSubmit={onSubmit} />
      ) : (
        <PhishCampaignEmailCreateFormGeneral
          form={form}
          itemRefs={itemRefs}
          createList={createList}
          setCreateList={setCreateList}
          onSubmit={onSubmit}
          onTestsubmit={onTestsubmit}
        />
      )}
    </Container>
  )
}

export default connect(mapStateToProps)(PhishCampaignEmailCreate)
